import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const PRODUCT_GALLERY_DESKTOP_QUERY = '(min-width: 750px)';
const KERVELAN_QUICK_ADD_MOBILE_QUERY = '(max-width: 749px)';
const KERVELAN_QUICK_ADD_COLOR_GALLERY_READY = 'kv:qv-color-gallery-ready';
const PRODUCT_GALLERY_SLIDE_SELECTOR = 'slideshow-component slideshow-slides > slideshow-slide';
const PRODUCT_GALLERY_THUMB_SELECTOR =
  "slideshow-controls[thumbnails][pagination-position='left'] .slideshow-controls__thumbnail";

function getSlideMediaId(slide) {
  return slide.querySelector('.product-media[data-media-id]')?.getAttribute('data-media-id') || '';
}

function getKervelanQuickAddVariantMediaMode(gallery) {
  if (!window.matchMedia(KERVELAN_QUICK_ADD_MOBILE_QUERY).matches) return null;
  if (!document.querySelector('main[data-template^="index"] .kv-featured-items')) return null;

  const dialog = gallery.closest('dialog.quick-add-modal');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.closest('#quick-add-dialog')) return null;

  const mode = dialog.dataset.kvQvVariantMediaMode;
  return mode === 'size' || mode === 'color' ? mode : null;
}

function clearKervelanQuickAddVariantMediaMode(gallery) {
  const dialog = gallery.closest('dialog.quick-add-modal');
  if (dialog instanceof HTMLDialogElement) delete dialog.dataset.kvQvVariantMediaMode;
}

function createKervelanLocalColorGallery(currentGallery, featuredMediaId) {
  if (!featuredMediaId) return null;

  const clone = currentGallery.cloneNode(true);
  if (!(clone instanceof Element)) return null;

  const slides = Array.from(clone.querySelectorAll(PRODUCT_GALLERY_SLIDE_SELECTOR));
  const targetSlide = slides.find((slide) => getSlideMediaId(slide) === featuredMediaId);
  const track = targetSlide?.parentElement;
  if (!(targetSlide instanceof Element) || !(track instanceof Element)) return null;

  track.prepend(targetSlide);
  Array.from(track.children)
    .filter((node) => node instanceof Element && node.matches('slideshow-slide.product-media-container'))
    .forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.toggleAttribute('data-kv-qv-active-media', index === 0);
    });

  return clone;
}

function getSelectedKervelanColorInput(target) {
  if (!(target instanceof Element)) return null;

  return (
    Array.from(target.querySelectorAll('input:checked[data-featured-media-id][data-option-name]')).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      const optionName = String(input.dataset.optionName || '').toLowerCase();
      return optionName.includes('color') || optionName.includes('colour');
    }) || null
  );
}

function shouldKeepMobilePdpGalleryForColor(gallery, target) {
  if (!window.matchMedia(KERVELAN_QUICK_ADD_MOBILE_QUERY).matches) return false;
  if (!gallery.closest('#MainContent') || gallery.closest('quick-add-dialog')) return false;

  const colorInput = getSelectedKervelanColorInput(target);
  const featuredMediaId = colorInput?.dataset.featuredMediaId || '';
  if (!featuredMediaId) return false;

  return Array.from(gallery.querySelectorAll(PRODUCT_GALLERY_SLIDE_SELECTOR)).some(
    (slide) => getSlideMediaId(slide) === String(featuredMediaId)
  );
}

function preserveDesktopProductGalleryOrder(currentGallery, newGallery) {
  if (!window.matchMedia(PRODUCT_GALLERY_DESKTOP_QUERY).matches) return;
  if (!currentGallery.closest('#MainContent') || currentGallery.closest('quick-add-dialog')) return;

  const currentSlides = Array.from(currentGallery.querySelectorAll(PRODUCT_GALLERY_SLIDE_SELECTOR));
  const newSlides = Array.from(newGallery.querySelectorAll(PRODUCT_GALLERY_SLIDE_SELECTOR));
  if (!currentSlides.length || !newSlides.length) return;

  const stableOrder = currentSlides.map(getSlideMediaId).filter(Boolean);
  if (!stableOrder.length) return;

  const slideshow =
    newGallery.querySelector(':scope > slideshow-component') || newGallery.querySelector('slideshow-component');
  const initialIndex = Number.parseInt(slideshow?.getAttribute('initial-slide') || '0', 10);
  const initialMediaId = Number.isFinite(initialIndex) ? getSlideMediaId(newSlides[initialIndex]) : '';

  const newThumbs = Array.from(newGallery.querySelectorAll(PRODUCT_GALLERY_THUMB_SELECTOR));
  const slideParent = newSlides[0]?.parentElement;
  const thumbParent = newThumbs[0]?.parentElement;
  if (!slideParent) return;

  const entries = newSlides.map((slide, index) => ({
    id: getSlideMediaId(slide),
    slide,
    thumb: newThumbs[index] || null,
    originalIndex: index,
  }));
  const byId = new Map(entries.filter((entry) => entry.id).map((entry) => [entry.id, entry]));
  const orderedEntries = [];
  const used = new Set();

  stableOrder.forEach((id) => {
    const entry = byId.get(id);
    if (!entry || used.has(entry)) return;
    orderedEntries.push(entry);
    used.add(entry);
  });

  entries.forEach((entry) => {
    if (used.has(entry)) return;
    orderedEntries.push(entry);
    used.add(entry);
  });

  const orderChanged = orderedEntries.some((entry, index) => entry.originalIndex !== index);
  if (!orderChanged) return;

  orderedEntries.forEach((entry, index) => {
    slideParent.appendChild(entry.slide);

    const clickAction = entry.slide.getAttribute('on:click');
    if (clickAction && /\/open\/\d+/.test(clickAction)) {
      entry.slide.setAttribute('on:click', clickAction.replace(/\/open\/\d+/, `/open/${index}`));
    }

    if (entry.thumb && thumbParent) {
      thumbParent.appendChild(entry.thumb);
      entry.thumb.setAttribute('on:click', `/select/${index}`);
    }
  });

  if (slideshow instanceof HTMLElement && initialMediaId) {
    const reorderedInitialIndex = orderedEntries.findIndex((entry) => entry.id === initialMediaId);
    if (reorderedInitialIndex >= 0) slideshow.setAttribute('initial-slide', String(reorderedInitialIndex));
  }
}

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const kervelanVariantMediaMode = getKervelanQuickAddVariantMediaMode(this);

    if (kervelanVariantMediaMode === 'size') {
      clearKervelanQuickAddVariantMediaMode(this);
      return;
    }

    if (shouldKeepMobilePdpGalleryForColor(this, event.target)) {
      return;
    }

    if (kervelanVariantMediaMode === 'color') {
      const dialog = this.closest('dialog.quick-add-modal');
      const requestId = dialog instanceof HTMLDialogElement ? dialog.dataset.kvQvColorRequestId || '' : '';
      const colorInput = getSelectedKervelanColorInput(event.target);
      const featuredMediaId = colorInput?.dataset.featuredMediaId || '';
      const localGallery = createKervelanLocalColorGallery(this, featuredMediaId);
      clearKervelanQuickAddVariantMediaMode(this);

      if (localGallery && dialog instanceof HTMLDialogElement && dialog.open) {
        dialog.dispatchEvent(
          new CustomEvent(KERVELAN_QUICK_ADD_COLOR_GALLERY_READY, {
            detail: { gallery: localGallery, requestId, source: 'local' },
          })
        );
        return;
      }

      event.promise
        .then(({ detail }) => {
          if (!detail?.html || !(dialog instanceof HTMLDialogElement) || !dialog.open) return;
          const newMediaGallery = detail.html.querySelector('media-gallery');
          if (!newMediaGallery) return;

          dialog.dispatchEvent(
            new CustomEvent(KERVELAN_QUICK_ADD_COLOR_GALLERY_READY, {
              detail: { gallery: newMediaGallery, requestId, source: 'server' },
            })
          );
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
        });
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        preserveDesktopProductGalleryOrder(this, newMediaGallery);
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
